import moment from 'moment';
import { ComposedDateMode } from '@/pages/intelligence/components/ComposedDatePicker/enums';
import { formatDateStrPlus } from '@intelligence/v3/utils/date';

import type { IDateType, IRegion } from '@/pages/intelligence/types/common';

export const transIsCountryToApi = (isCountry: boolean | 'all') => {
  // 0:不要country的数据 1:只要country数据 2:数据全返回
  switch (isCountry) {
    case true:
      return 2; // 应该是1，2多返回数据总量
    case false:
      return 0;
    case 'all':
      return 2;
    default:
      return 0;
  }
};

export const calMin = (list: any) => {
  // 对排名来说是值越小排名越大
  if (list.length <= 1) {
    return list;
  }
  const arr = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (const i in list) {
    // 0 i游戏 -- 0 1 2 3 榜单
    const item = list[i];
    const itemList = item?.rank_list;
    for (const j in itemList) {
      const itemPerio = itemList[j]; // 榜单
      if (Number(itemPerio?.value) === 0 || isNaN(Number(itemPerio?.value))) {
        // Do nothing
      } else if (arr[j] === 0) {
        arr[j] = itemPerio?.value;
      } else if (Number(itemPerio?.value) < arr[j]) {
        arr[j] = itemPerio?.value;
      }
    }
  }
  for (const i in list) {
    const item = list[i];
    const itemList = item?.rank_list;
    for (const j in itemList) {
      const itemPerio = itemList[j];
      if (arr[j] === itemPerio?.value) {
        itemPerio.isMax = true;
      }
    }
  }
  return list;
};

export const calMax = (list: any[]) => {
  if (!Array.isArray(list)) return [];
  if (list?.length <= 1) {
    return list;
  }

  const arr = Array.prototype.fill.call(new Array(12), 0);

  for (const i in list) {
    const item = list[i];
    const itemList = item?.metric_list || item?.rank_list;
    for (const j in itemList) {
      const itemPerio = itemList[j];
      if (Number(itemPerio?.value) > arr[j]) {
        arr[j] = itemPerio?.value;
      }
    }
  }

  for (const i in list) {
    const item = list[i];
    const itemList = item?.metric_list || item?.rank_list;
    for (const j in itemList) {
      const itemPerio = itemList[j];
      if (arr[j] === itemPerio?.value) {
        itemPerio.isMax = true;
      }
    }
  }
  return list;
};

const isOverToday = (time: string) => {
  const today = moment().format('YYYY-MM-DD');
  if (!time || !moment(time).isValid()) {
    return today;
  }
  return today > time ? time : today;
};

export const getGameTime = (gameList: any[], timetype: string, gameid: string, datetype: string, daterange: any) => {
  const gameData = Array.isArray(gameList) && gameList.find((item) => item?.game_id === gameid);
  if (!gameData) return '';
  let releaseTime = '';
  let startDay = '';
  let endDay = '';
  if (!gameData?.release_time || gameData?.release_time === '') {
    const mRelease = moment()
      .subtract(daterange[1] * 1 - daterange[0] * 1, datetype === 'Day' ? 'days' : 'months');
    releaseTime = formatDateStrPlus(mRelease);
  } else {
    releaseTime = formatDateStrPlus(gameData?.release_time);
  }
  if (!releaseTime) return '';
  const mStart = moment(releaseTime)
    .add(daterange[0] * 1 - 1, datetype === 'Day' ? 'days' : 'months');
  startDay = formatDateStrPlus(mStart);
  startDay = isOverToday(startDay);
  const mEnd = moment(releaseTime)
    .add(daterange[1] * 1 - 1, datetype === 'Day' ? 'days' : 'months');
  endDay = formatDateStrPlus(mEnd);
  endDay = isOverToday(endDay);
  if (timetype === 'start_date') {
    if (!startDay) return '';
    return datetype === 'Day' ? startDay : `${formatDateStrPlus(startDay, 'YYYY-MM')}-01`;
  }
  if (!endDay) return '';
  return datetype === 'Day' ? endDay : `${formatDateStrPlus(endDay, 'YYYY-MM')}-01`;
};

export const getDownloadDates = (gameList: any[], isRangeDayMonth: boolean, type: string, ranges: string[]): any[] => {
  if (!Array.isArray(gameList)) return [];
  const result: any[] = [];
  let game: any;
  let newGame: any;
  let releaseTime: any;
  for (let i = 0; i < gameList.length; i++) {
    game = gameList[i];
    if (!game) continue;
    releaseTime = moment(game.release_time);
    releaseTime = releaseTime.isValid() ? releaseTime.format('YYYY-MM-DD') : '';
    newGame = {
      unified_id: game.game_id,
      release_time: releaseTime,
      start_date: isRangeDayMonth ? getGameTime(gameList, 'start_date', game.game_id, type, ranges) : '',
      end_date: isRangeDayMonth ? getGameTime(gameList, 'end_date', game.game_id, type, ranges) : '',
    };
    result.push(newGame);
  }
  return result;
};

export const getIsRangeDayMonth = (mode: ComposedDateMode): boolean => mode === ComposedDateMode.Relative;

const OTHER_DATE_FORMATS: {
  [key in IDateType]: 'Day' | 'Week' | 'Month' | 'Year';
} = {
  date: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

type Platform = 'ios' | 'android';
const PLATFORMS_DATA: any[] = [
  { platform: 'ios', storeNameKeyword: 'appstore' },
  { platform: 'android', storeNameKeyword: 'googleplay' },
];
/**
 * 指定app store名列表 转换为 platform平台名列表，默认platform名不会重复
 * @param storeNames app store 商店名列表
 * @param isDuplicate 转换结果中，是否允许platform名重复
 * @returns platform平台名列表
 */
export const getPlatformByStoreNames = (storeNames: string[], isDuplicate = false): Platform[] => {
  if (!Array.isArray(storeNames)) return [];
  const platforms: Platform[] = [];
  let storeName: string;
  let target: any;
  let platform: Platform;
  for (let i = 0; i < storeNames.length; i++) {
    storeName = storeNames[i];
    if (!storeName) continue;
    target = PLATFORMS_DATA.find((item) => storeName.startsWith(item.storeNameKeyword));
    if (!target) continue;
    platform = target.platform;
    if (isDuplicate || !platforms.includes(platform)) {
      platforms.push(platform);
    }
  }
  return platforms;
};

export const getOtherDateFormat = (dateType: IDateType): 'Day' | 'Week' | 'Month' | 'Year' =>
  OTHER_DATE_FORMATS[dateType] || OTHER_DATE_FORMATS.date;

export const handleStoreRankMulListFormat = (list) => {
  // const tableCloumnsValues = [];//集中游戏ID 即游戏列数
  const rankTypeList: string[] = ['1-3', '4-10', '11-50', '51-200'];
  const compare_list = [];
  for (const key in rankTypeList) {
    const tmp: any = {};
    const regionsList: string[] = [];
    const regionCountys: any[] = [];
    const valArr: number[] = [];
    list?.forEach((item, idx) => {
      // tableCloumnsValues.push(item?.game_id)
      // 第N个游戏的第K个类型值
      let rankListItem =
        Array.isArray(item?.rank_list) && item?.rank_list?.filter((t) => t?.rank_type === rankTypeList[key])[0];
      if (!rankListItem) {
        rankListItem = {
          rank_type: rankTypeList[key],
          region_countys: [],
        };
      }
      tmp.rank_type = rankListItem?.rank_type;
      tmp[`value_game${idx}`] = rankListItem?.value;
      valArr.push(isNaN(Number(rankListItem?.value)) ? 0 : Number(rankListItem?.value));
      // tmp[`isMax${idx}`] = rankListItem?.isMax;

      // 获取多个游戏中的国家集合
      rankListItem?.region_countys?.forEach((regionItem) => {
        if (regionsList.indexOf(regionItem?.region) === -1) {
          regionsList.push(regionItem?.region);
        }
      });

      regionsList?.forEach((item) => {
        // 为每个rankList添加CoutrieList
        const coutrie = rankListItem?.region_countys?.filter((t) => t.region === item);

        let regionCountysItem = {};
        for (let i = 0; i < regionCountys?.length; i++) {
          const t = regionCountys[i];
          if (t.region === item) {
            regionCountysItem = t;
            regionCountys.splice(i, 1);
            break;
          }
        }

        regionCountys.push({
          ...regionCountysItem,
          region: item,
          [`countrys_game${idx}`]: coutrie[0]?.countrys || null,
        });
      });
      // 如何没有数据则全展示空，占行
      if (idx === list?.length - 1 && regionsList?.length === 0) {
        regionCountys.push({
          region: '',
        });
      }
    });

    // 每一行高亮
    const max = Math.max(...valArr);
    valArr.map((item, i) => {
      if (item === max) {
        tmp[`isMax${i}`] = true;
      }
    });
    compare_list.push({
      ...tmp,
      region_countys: regionCountys,
    });
  }
  return compare_list;
};

export const changeMulRankTitle = (str: string) => {
  const t: any = {
    '1-3': '1~3',
    '4-10': '4~10',
    '11-50': '11~50',
    '51-200': '51~200',
  };
  return t[str] || str;
};

export const getRegionsOptionsByStores = (
  stores?: string[],
  regionsOptionsMap?: {
    [key: string]: IRegion[];
  },
): IRegion[][] => {
  if (!Array.isArray(stores)) return [];
  return stores.map((store) => regionsOptionsMap?.[store] || []).filter((regionsOptions) => regionsOptions?.length);
};

// 合并多个store商店的 国家列表数据，以及所有国家code列表
export const getMergedRegionsOptionsAndAllCountries = (
  regionsOptionsList: IRegion[][],
): {
  regionsOptions: IRegion[];
  allCountries: string[];
} => {
  let regionsOptionsResult: IRegion[] = [];
  let allCountriesResult: string[] = [];

  try {
    // 遍历app商店、地区国家列表数据 映射集合
    for (const regionsOptions of regionsOptionsList) {
      if (!Array.isArray(regionsOptions) || !regionsOptions.length) continue;
      // 遍历地区集合
      for (const regionOption of regionsOptions) {
        if (!regionOption?.area_code) continue;
        // 判断返回结果中 是否包含 当前地区
        let regionOptionOfResult = regionsOptionsResult.find(
          (regionsOptionsResultItem) => regionsOptionsResultItem?.area_code === regionOption?.area_code,
        );
        // 返回结果中 不包含当前地区，就把 新建的当前地区 push 到结果集
        if (!regionOptionOfResult) {
          regionOptionOfResult = {
            area_code: regionOption?.area_code,
            area_name_en: regionOption?.area_name_en,
            area_name_ch: regionOption?.area_name_ch,
            country_set: [],
          };
          regionsOptionsResult.push(regionOptionOfResult);
        }
        const countries = regionOption?.country_set;
        const countriesOfResult = regionOptionOfResult?.country_set;
        // 遍历当前地区下的 国家集合
        for (const country of countries) {
          if (!country?.country_code) continue;
          const countryOfResult = countriesOfResult.find(
            (countryOfResultItem) => countryOfResultItem?.country_code === country.country_code,
          );
          if (!countryOfResult) {
            countriesOfResult.push(country);
            allCountriesResult.push(country.country_code);
          }
        }
      }
    }
  } catch (exception) {
    regionsOptionsResult = [];
    allCountriesResult = [];
  }
  return {
    regionsOptions: regionsOptionsResult,
    allCountries: allCountriesResult,
  };
};

export const getMergedRegionsOptionsAndAllCountriesByStores = (
  ...args: Parameters<typeof getRegionsOptionsByStores>
): ReturnType<typeof getMergedRegionsOptionsAndAllCountries> => {
  const regionsOptionsList = getRegionsOptionsByStores(...args);
  return getMergedRegionsOptionsAndAllCountries(regionsOptionsList);
};

export const getStoresWhenCountryTypeChange = (
  stores?: string[],
  props?: {
    isOperate?: boolean;
    countryType?: 'single' | 'multiple' | string;
    defaultSingleStore?: string;
    defaultMultipleStores?: string[];
  },
): string[] => {
  const {
    isOperate = false,
    countryType = 'multiple',
    defaultSingleStore = '',
    defaultMultipleStores = [] as string[],
  } = props || {};

  // 没操作过筛选器，直接返回默认的store
  if (!isOperate) {
    return countryType === 'single' ? defaultMultipleStores : [defaultSingleStore];
  }

  const singleStore = stores?.[0] || defaultSingleStore;
  const multipleStores = stores?.length ? stores : defaultMultipleStores;
  return countryType === 'single' ? multipleStores : [singleStore];
};
